import React, { useEffect, useState } from 'react';
import TitleDivider from 'components/TitleDivider';
import GenericForm from 'skyone-lib/lib/GenericForm';
import {
  ModelTypeEnum,
  EnvironmentFields,
  FormModelProps,
  ModelFormFields,
  ProviderResourcesProps,
} from 'modules/environments/interfaces';
import { getProviderResourcesRequest } from 'modules/environments/actions';
import { FormikErrors } from 'formik';
import i18n from 'i18n';
import { applyI18n } from 'utils/helpers';
import { GenericFormItem } from 'skyone-lib/lib/GenericForm/interfaces';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { RolesEnum } from 'modules/authenticate/interfaces';
import { createFieldOptions } from '../utils';
import {
  getLicenseTypeDisplayValue,
  LicenseTypeFormValue,
  toLicenseTypeFormValue,
  toLicenseTypePayloadValue,
} from '../licenseTypeDisplay';
import screenFields from './ociFields';
import {
  getModelTypeByPublicationType,
  getProviderFieldByPublicationType,
} from '../cloudProviderUtils';

interface OciFormProps {
  values: ModelFormFields | FormModelProps;
  errors: FormikErrors<ModelFormFields | FormModelProps>;
  providerResources: ProviderResourcesProps;
  isEdit: boolean;
  modelType: any;
  environment: EnvironmentFields | null;
  modelSelected: ModelFormFields | null;
  role?: RolesEnum;
  getProviderResourcesRequest: (props) => void;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
}

const OciForm = ({
  values,
  errors,
  providerResources,
  isEdit,
  modelType,
  environment,
  modelSelected,
  role,
  getProviderResourcesRequest,
  setFieldValue,
}: OciFormProps) => {
  const [ociFields, setOciFields] = useState<any>([]);
  const isSuperAdmin = role === RolesEnum.superAdmin;

  const providerDataModelType = getModelTypeByPublicationType(
    isEdit ? modelType : environment?.publication?.type
  );

  const mapOciFields = {
    'provider_data.boot_volume_backup_id': providerDataModelType,
    'provider_data.boot_volume_id': providerDataModelType,
    'provider_data.image_id': providerDataModelType,
    'provider_data.subnet': 'subnets',
    'provider_data.zone': 'zones',
    'provider_data.flavor': 'flavors',
    'provider_data.security_group': 'securityGroups',
  };

  const makeFields = () =>
    screenFields(isEdit ? modelType : environment?.publication?.type, {
      isEdit,
      isSuperAdmin,
    }).map(field => {
      if (mapOciFields[field?.stateKey]) {
        return createFieldOptions(field, mapOciFields, providerResources);
      }

      return field;
    });

  const ociScreenFields = (
    values: ModelFormFields | FormModelProps
  ): GenericFormItem[] =>
    applyI18n(
      ociFields.map(field => {
        if (field.stateKey === 'provider_data.flavor') {
          const types = ['boot_volume_id', 'boot_volume_backup_id', 'image_id'];
          if (!types.some(type => values?.provider_data?.[type])) {
            return {
              ...field,
              disabled: true,
            };
          }
        }
        return field;
      }),
      i18n.language
    );

  const getProviderField = React.useCallback(
    (type?: ModelTypeEnum) =>
      getProviderFieldByPublicationType(modelSelected)(type),
    [modelSelected]
  );

  useEffect(() => {
    setOciFields(makeFields());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerResources]);

  React.useEffect(() => {
    getProviderResourcesRequest({
      environment,
      modelType: isEdit ? modelType : environment?.publication?.type,
      reloadEnvProviderResources:
        providerResources.environment_uuid !== environment?.environment_uuid,
      customId: isEdit && getProviderField(modelType).value,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelSelected]);

  const ociFieldsForDisplay = React.useMemo(() => {
    if (isEdit) {
      return {
        ...values,
        provider_data: {
          ...values.provider_data,
          license_type: toLicenseTypeFormValue(
            values?.provider_data?.license_type
          ),
        },
      };
    }
    return {
      ...values,
      provider_data: {
        ...values.provider_data,
        license_type: getLicenseTypeDisplayValue(
          values?.provider_data?.license_type
        ),
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, i18n.language, isEdit]);

  return (
    <>
      <TitleDivider overrideClasses={{ title: { marginTop: 8 } }}>
        {`${i18n.t('DYNAMIC_FILTER.OCI_KEY')}: `}
      </TitleDivider>
      <GenericForm
        fields={ociFieldsForDisplay}
        screenFields={ociScreenFields(values)}
        setFieldValue={(key, value) => {
          if (
            [
              'provider_data.boot_volume_id',
              'provider_data.image_id',
              'provider_data.boot_volume_backup_id',
            ].includes(key)
          ) {
            const type = key?.split('.')[1];

            if (values?.provider_data?.[type] !== value) {
              const newFields = ociFields.map(field => {
                if (field.stateKey === 'provider_data.flavor') {
                  return {
                    ...field,
                    options: [],
                    disabled: true,
                  };
                }

                return field;
              });
              setOciFields(newFields);
              getProviderResourcesRequest({
                environment,
                modelType: isEdit ? modelType : environment?.publication?.type,
                customId: value,
                reloadEnvProviderResources:
                  providerResources.environment_uuid !==
                  environment?.environment_uuid,
              });
            }
          }
          if (key === 'provider_data.license_type') {
            setFieldValue(
              key,
              toLicenseTypePayloadValue(value as LicenseTypeFormValue)
            );
            return;
          }
          setFieldValue(key, value);
        }}
        errors={errors}
      />
    </>
  );
};

const mapStateToProps = ({
  environments: { environmentSelected, providerResources, modelSelected },
  authenticate: { data },
}: ApplicationState) => ({
  environment: environmentSelected,
  providerResources,
  modelSelected,
  role: data?.role,
});

export default connect(mapStateToProps, {
  getProviderResourcesRequest,
})(OciForm);
